/**
 * FinanceDashboard Component - Hedita Design System
 * Feature: Finance - Dashboard panoramica finanze
 * Stile: Premium Orange Dark Theme
 */

import { useState, useMemo } from "react";
import { TrendingUp, TrendingDown, DollarSign, BarChart3, Percent, CalendarDays, ChevronDown, Plus, ArrowUpRight, ArrowDownRight } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { useTransazioni, useFinanzeSummary } from "../hooks";
import type { Transazione } from "../types";

const MESI = ["Gen","Feb","Mar","Apr","Mag","Giu","Lug","Ago","Set","Ott","Nov","Dic"];

function KPICard({ title, value, sub, trend, type = "neutral" }: {
  title: string; 
  value: string; 
  sub?: string; 
  trend?: number;
  type?: "positive" | "negative" | "neutral";
}) {
  const config = {
    positive: { bg: "bg-emerald-500/10", color: "text-emerald-400", border: "border-emerald-500/20", icon: ArrowUpRight },
    negative: { bg: "bg-rose-500/10", color: "text-rose-400", border: "border-rose-500/20", icon: ArrowDownRight },
    neutral: { bg: "bg-primary/10", color: "text-primary", border: "border-primary/20", icon: DollarSign },
  };
  const cfg = config[type];
  const Icon = cfg.icon;

  return (
    <div className="bg-card border border-border rounded-2xl p-5 hover:border-primary/20 transition-all">
      <div className="flex items-start justify-between mb-3">
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{title}</span>
        <div className={`w-9 h-9 rounded-xl ${cfg.bg} ${cfg.color} border ${cfg.border} flex items-center justify-center`}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
      <div className={`text-2xl font-bold ${cfg.color}`}>{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
      {trend !== undefined && (
        <div className={`flex items-center gap-1 mt-2 text-xs font-medium ${trend >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
          {trend >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
          {trend >= 0 ? "+" : ""}{trend}% vs prec.
        </div>
      )}
    </div>
  );
}

export function FinanceDashboard() {
  const [anno, setAnno] = useState(new Date().getFullYear());
  const [mese, setMese] = useState<number | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [formTipo, setFormTipo] = useState<"entrata" | "uscita">("entrata");
  
  const { data: transazioni = [], isLoading } = useTransazioni();
  const { data: summary, isLoading: summaryLoading } = useFinanzeSummary();

  const fmt = (n: number) => "€" + (n || 0).toLocaleString("it-IT", { minimumFractionDigits: 0, maximumFractionDigits: 0 });

  // Filter by year/month
  const filtered = useMemo(() => {
    return transazioni.filter(t => {
      if (!t.data) return false;
      const d = new Date(t.data);
      if (d.getFullYear() !== anno) return false;
      if (mese !== null && d.getMonth() !== mese) return false;
      return true;
    });
  }, [transazioni, anno, mese]);

  const entrate = filtered.filter(t => t.tipo === 'entrata').reduce((s, t) => s + (t.importo || 0), 0);
  const uscite = filtered.filter(t => t.tipo === 'uscita').reduce((s, t) => s + (t.importo || 0), 0);
  const bilancio = entrate - uscite;

  const handleNewEntrata = () => {
    setFormTipo("entrata");
    setFormOpen(true);
  };

  const handleNewUscita = () => {
    setFormTipo("uscita");
    setFormOpen(true);
  };

  if (isLoading || summaryLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-48" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[1,2,3,4].map(i => <Skeleton key={i} className="h-32 rounded-xl" />)}
        </div>
        <Skeleton className="h-96 rounded-2xl" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-foreground flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-primary" />
            Finanze
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">Panoramica entrate e uscite</p>
        </div>
        <div className="flex gap-2">
          <button 
            onClick={handleNewUscita}
            className="flex items-center gap-2 bg-rose-500/10 text-rose-400 border border-rose-500/20 px-4 py-2.5 rounded-xl text-sm font-medium hover:bg-rose-500/20 transition-all"
          >
            <TrendingDown className="h-4 w-4" /> Uscita
          </button>
          <button 
            onClick={handleNewEntrata}
            className="flex items-center gap-2 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 px-4 py-2.5 rounded-xl text-sm font-medium hover:bg-emerald-500/20 transition-all"
          >
            <TrendingUp className="h-4 w-4" /> Entrata
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPICard 
          title="Entrate" 
          value={fmt(entrate)} 
          sub={`${filtered.filter(t => t.tipo === 'entrata').length} transazioni`}
          type="positive"
        />
        <KPICard 
          title="Uscite" 
          value={fmt(uscite)} 
          sub={`${filtered.filter(t => t.tipo === 'uscita').length} transazioni`}
          type="negative"
        />
        <KPICard 
          title="Bilancio" 
          value={fmt(bilancio)} 
          sub={bilancio >= 0 ? "In attivo" : "In passivo"}
          type={bilancio >= 0 ? "positive" : "negative"}
        />
        <KPICard 
          title="Margine" 
          value={entrate > 0 ? ((bilancio / entrate) * 100).toFixed(1) + "%" : "0%"} 
          sub="su entrate"
          type="neutral"
        />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 p-3 bg-secondary/30 rounded-xl border border-border/50">
        <div className="flex items-center gap-2">
          <CalendarDays className="h-4 w-4 text-muted-foreground" />
          <select 
            value={anno}
            onChange={e => setAnno(Number(e.target.value))}
            className="px-3 py-1.5 rounded-lg border border-border bg-card text-sm focus:outline-none focus:border-primary/50"
          >
            {[2023, 2024, 2025, 2026].map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        <div className="flex gap-1">
          <button 
            onClick={() => setMese(null)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
              mese === null ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground"
            }`}
          >
            Tutto
          </button>
          {MESI.map((m, i) => (
            <button 
              key={m}
              onClick={() => setMese(mese === i ? null : i)}
              className={`px-2 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                mese === i ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground"
              }`}
            >
              {m}
            </button>
          ))}
        </div>
      </div>

      {/* Recent Transactions */}
      <div className="bg-card border border-border rounded-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
          <h3 className="font-semibold text-foreground">Transazioni recenti</h3>
          <button 
            onClick={() => window.location.href = "/finanze/transazioni"}
            className="text-xs text-primary hover:text-primary/80 transition-colors"
          >
            Vedi tutte
          </button>
        </div>
        
        {filtered.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-16 h-16 rounded-2xl bg-secondary/50 border border-border/50 flex items-center justify-center mx-auto mb-4">
              <DollarSign className="h-8 w-8 text-muted-foreground/40" />
            </div>
            <p className="font-medium text-foreground mb-1">Nessuna transazione</p>
            <p className="text-sm text-muted-foreground mb-4">Registra la prima transazione</p>
            <button 
              onClick={handleNewEntrata}
              className="px-4 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-all"
            >
              <Plus className="h-4 w-4 inline mr-1" /> Nuova
            </button>
          </div>
        ) : (
          <div className="divide-y divide-border/50">
            {filtered.slice(0, 10).map((t: Transazione) => (
              <div 
                key={t.id} 
                onClick={() => window.location.href = `/finanze/transazioni/${t.id}`}
                className="px-5 py-4 hover:bg-secondary/30 transition-all cursor-pointer flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    t.tipo === 'entrata' 
                      ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20" 
                      : "bg-rose-500/10 text-rose-400 border border-rose-500/20"
                  }`}>
                    {t.tipo === 'entrata' ? <ArrowUpRight className="h-5 w-5" /> : <ArrowDownRight className="h-5 w-5" />}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">{t.descrizione || t.categoria}</p>
                    <p className="text-xs text-muted-foreground">
                      {t.data ? new Date(t.data).toLocaleDateString("it-IT") : "—"} · {t.categoria}
                      {t.struttura_nome && ` · ${t.struttura_nome}`}
                    </p>
                  </div>
                </div>
                <div className={`text-sm font-semibold ${
                  t.tipo === 'entrata' ? "text-emerald-400" : "text-rose-400"
                }`}>
                  {t.tipo === 'entrata' ? "+" : "-"}{fmt(t.importo || 0)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Form Drawer */}
      <TransazioneForm
        open={formOpen}
        onOpenChange={setFormOpen}
        defaultTipo={formTipo}
      />
    </div>
  );
}
